/**
 * match_assignments row → Payment, shared by usePayments (Virements) and
 * useInfinitePayments (Finances), plus the accounting league filter both apply.
 *
 * Payment.leagueId is the ACCOUNTING league (lib/leagueScope/matchScope.ts
 * #getMatchAccountingLeagueId): the group league of a regular match, the
 * organising league of an external competition match organised by a regional /
 * wilaya league. Other external matches (LIRF, LNFA… organised, or none) are
 * not payments: the mapper drops them.
 */
import { AccountingStatus, Payment } from '../../types';
import {
    buildAccountingExternalMatchesOrFilter,
    buildAccountingLeagueFilter,
    type LeagueLevelRef,
} from '../leagueScope/matchScope';
import { resolveAccountingLeagueId } from '../../supabase/functions/_shared/accountingLeagueScope';
import { isCupMatchRow } from '../../supabase/functions/_shared/disciplinary/cupMatches';

/**
 * Columns of the `matches` embed needed to resolve the accounting league
 * (add inside the embed of a match_assignments select).
 */
export const MATCH_ACCOUNTING_LEAGUE_COLUMNS = `
            is_external_competition,
            originating_league_id,
            originatingLeague:leagues!matches_originating_league_id_fkey(id,level)`;

/** Accounting league of a raw match embed (snake_case row); null = out of accounting. */
export function accountingLeagueIdOfMatchRow(match: any): string | null {
    const origin = match?.originatingLeague;
    return resolveAccountingLeagueId(
        {
            isExternalCompetition: match?.is_external_competition,
            originatingLeagueId: match?.originating_league_id,
            groupLeagueId: match?.leagueGroup?.league?.id ?? match?.leagueGroup?.league_id ?? null,
        },
        (id) => (origin && origin.id === id ? origin.level : undefined),
    );
}

export function mapMatchAssignmentToPayment(a: any): Payment | null {
    if (!a.official_id || a.is_not_assigned || a.is_absent) {
        return null;
    }

    const match = a.match;
    const accountingLeagueId = accountingLeagueIdOfMatchRow(match);
    // External match outside every accounting (LIRF-organised Coupe d'Algérie…).
    if (match?.is_external_competition && !accountingLeagueId) return null;

    const official = a.official;
    const indemnity = Number(a.indemnity_amount ?? 0);
    const travelKm = Number(a.travel_distance_km ?? 0);
    const distanceBonusValue = a.distance_bonus !== null ? Number(a.distance_bonus) : (travelKm >= 1000 ? 10000 : 0);

    const accountingStatus = (match?.accounting_status as AccountingStatus) || AccountingStatus.NOT_ENTERED;
    const isFinalized = accountingStatus === AccountingStatus.VALIDATED || accountingStatus === AccountingStatus.CLOSED;

    const rawIrg = a.irg_amount !== null ? Number(a.irg_amount) : 0;
    const irgAmount = (isFinalized || rawIrg !== 0) ? (a.irg_amount !== null ? Number(a.irg_amount) : null) : null;

    const rawTotal = a.net_amount !== null ? Number(a.net_amount) : 0;
    const total = isFinalized ? rawTotal : 0;

    const rawBase = a.base_indemnity !== null ? Number(a.base_indemnity) : 0;
    const baseIndemnity = (isFinalized || rawBase !== 0) ? (a.base_indemnity !== null ? Number(a.base_indemnity) : null) : null;

    return {
        id: a.id,
        officialId: a.official_id,
        matchId: a.match_id,
        missionId: undefined,
        assignmentType: 'match',
        leagueId: accountingLeagueId || '',
        groupId: match?.leagueGroup?.id || match?.league_group_id || '',
        gameDay: match?.game_day ?? null,
        matchDescription: match ? `${match.homeTeam?.name || ''} vs ${match.awayTeam?.name || ''}` : '',
        matchDate: match?.match_date || '',
        officialName: official?.full_name || [official?.first_name, official?.last_name].filter(Boolean).join(' ') || '',
        officialNameAr: [official?.first_name_ar, official?.last_name_ar].filter(Boolean).join(' ') || null,
        officialCategory: official?.category ?? null,
        bankAccountNumber: official?.bank_account_number || null,
        role: a.role,
        indemnity,
        baseIndemnity,
        travelDistanceInKm: travelKm,
        distanceBonus: distanceBonusValue,
        irgAmount,
        total,
        accountingStatus,
        notes: a.notes || null,
        originalOfficialId: a.original_official_id || null,
        validatedByUserId: match?.validated_by || null,
        validatedAt: match?.validated_at || null,
        validatedByName: match?.validatedBy?.full_name || undefined,
        rejectionReason: match?.rejection_reason ?? null,
        rejectionComment: match?.rejection_comment ?? null,
        isPaid: a.is_paid || false,
        paidAt: a.paid_at || null,
        paymentBatchId: a.payment_batch_id || null,
        paymentBatchReference: a.paymentBatchInfo?.payment_batches?.reference || null,
        paymentMonthOverride: a.payment_month_override || null,
        // Embed leagueGroup avec `kind` (usePayments / useInfinitePayments).
        isCupMatch: !!match && isCupMatchRow({
            is_external_competition: match.is_external_competition,
            league_group_kind: match.leagueGroup?.kind ?? null,
        }),
        cupRound: match?.cup_round ?? null,
    } as Payment;
}

/** `filters.leagueId` (one id or a list) as a clean list. */
export function leagueFilterIds(value: unknown): string[] {
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && !!v);
    return typeof value === 'string' && value ? [value] : [];
}

/**
 * Builder filters of a match_assignments read with the accounting league
 * scope: `leagueId` widened to the EXT_COMPETITIONS groups and
 * `matchExternalOrganiserIds` (SupabaseQueryBuilder → `matches.or=(…)`) when a
 * filtered league is regional / wilaya. Unchanged without league filter or
 * league list. Mission filters must keep reading the ORIGINAL `leagueId`.
 */
export function withAccountingLeagueScope(
    filters: Record<string, any>,
    leagues: readonly (LeagueLevelRef & { code?: string | null; name?: string | null })[] | null | undefined,
): Record<string, any> {
    const ids = leagueFilterIds(filters?.leagueId);
    if (ids.length === 0 || !leagues?.length) return filters;
    const scope = buildAccountingLeagueFilter(ids, leagues);
    if (scope.externalOrganiserIds.length === 0) return filters;
    return { ...filters, leagueId: scope.groupLeagueIds, matchExternalOrganiserIds: scope.externalOrganiserIds };
}

/**
 * Same filter applied to a hand-built match_assignments query (needs the
 * `matches!inner` + `league_groups!inner` embeds, like the builder path).
 */
export function applyMatchAssignmentLeagueScope<Q extends { eq: any; in: any; or: any }>(
    query: Q,
    filters: Record<string, any>,
): Q {
    const ids = leagueFilterIds(filters?.leagueId);
    let q: any = query;
    if (ids.length === 1) q = q.eq('matches.league_groups.league_id', ids[0]);
    else if (ids.length > 1) q = q.in('matches.league_groups.league_id', ids);
    const orFilter = ids.length ? buildAccountingExternalMatchesOrFilter(filters?.matchExternalOrganiserIds) : null;
    if (orFilter) q = q.or(orFilter, { referencedTable: 'matches' });
    return q as Q;
}
